import type { StudentSummary } from '@/services/api/student-summary';
import { fetchApi, fetchApiBlob } from '@/services/api-client';

export const REPORT_PERIODS_PATH = '/report-periods';
export const REPORT_CARDS_PATH = '/report-cards';
export const GRADING_SCALES_PATH = '/grading-scales';

export type ReportPeriodStatus = 'draft' | 'open' | 'closed' | 'published';
export type ReportCardLineKind = 'grade' | 'standards' | 'narrative';
/** Null both when there is nothing to compare against and when the two periods used different
 *  grading scales — see the API's `GradingScalesService.compare`. */
export type MarkMovement = 'up' | 'down' | 'level';

export type ReportPeriod = {
  id: string;
  schoolYearId: string;
  sequence: number;
  name: string;
  startDate: string;
  endDate: string;
  issueDate: string | null;
  entryOpensAt: string | null;
  dueAt: string | null;
  status: ReportPeriodStatus;
  publishedAt: string | null;
};

export type ReportCardLine = {
  id: string;
  gradeLevel: number;
  key: string;
  label: string;
  groupLabel: string | null;
  subjectId: string | null;
  subjectName: string | null;
  scaleId: string | null;
  scaleName: string | null;
  kind: ReportCardLineKind;
  description: string | null;
  commentEnabled: boolean;
  sortOrder: number;
  active: boolean;
  teacherIds: string[];
  teacherNames: string[];
};

export type GradingScaleValue = {
  id: string;
  label: string;
  title: string;
  descriptor: string | null;
  gpa: string | null;
  rank: number;
};

export type GradingScale = {
  id: string;
  code: string;
  name: string;
  description: string | null;
  values: GradingScaleValue[];
};

export type EntryScaleValue = { id: string; label: string; title: string };

/**
 * One earlier period's mark for the same subject, joined through the line's `key` + `gradeLevel`
 * rather than the period-specific `lineId`. `movement` is null both when there is nothing to
 * compare against and when the two periods used different grading scales; the old grade is still
 * shown either way.
 */
export type EntryHistoryCell = {
  periodId: string;
  periodName: string;
  valueLabel: string | null;
  comment: string | null;
  movement: MarkMovement | null;
};

export type EntryStudentRow = {
  cardId: string;
  studentId: string;
  photoUrl: string;
  firstName: string;
  lastName: string;
  nickname: string | null;
  gradeLevel: number;
  valueId: string | null;
  valueLabel: string | null;
  comment: string | null;
  /** No letter is given this period: the grade picker is off and the comment is the work. */
  commentOnly: boolean;
  history: EntryHistoryCell[];
};

/** Named after the API response, not `EntryGrid`, so it cannot collide with the grid component. */
export type EntryGridResponse = {
  lineId: string;
  label: string;
  gradeLevel: number;
  kind: ReportCardLineKind;
  scaleId: string | null;
  scaleValues: EntryScaleValue[];
  commentEnabled: boolean;
  outstanding: number;
  total: number;
  rows: EntryStudentRow[];
};

/** `all` is honoured only for the office; the API answers anyone else with their own lines. */
export type EntryScope = 'mine' | 'all';

/** `allOutstanding` is null for anyone but the office, which is how the tab knows to offer the switch. */
export type EntrySummary = {
  mineCount: number;
  mineOutstanding: number;
  allOutstanding: number | null;
};

export type CompletenessRow = {
  lineId: string;
  label: string;
  gradeLevel: number;
  teacherIds: string[];
  teacherNames: string[];
  filled: number;
  total: number;
};

export type NotifyPreview = { cards: number; parents: number; withoutRecipients: number };

export type PeriodCardViews = {
  cardId: string;
  student: StudentSummary;
  gradeLevel: number;
  parentsLinked: number;
  parentsOpened: number;
  pdfDownloaded: boolean;
  studentOpened: boolean;
  /** When a parent first opened it; null while no parent has. */
  firstOpenedAt: string | null;
};

export type PeriodViews = {
  cards: number;
  openedByParent: number;
  parentsOpened: number;
  pdfDownloaded: number;
  openedByStudent: number;
  children: PeriodCardViews[];
};
export type NotifyResult = { emails: number; cards: number; withoutRecipients: number; failed: number };

/** A publish, notify or reminder running in the background on the API; the admin polls it. */
export type ReportCardJob = {
  kind: 'publish' | 'notify' | 'remind';
  periodId: string;
  state: 'running' | 'done' | 'failed';
  done: number;
  total: number;
  error: string | null;
  result: unknown;
};

export type PeriodCard = {
  cardId: string;
  studentId: string;
  studentName: string;
  /** The nickname when there is one, else the full name. */
  displayName: string;
  photoUrl: string;
  status: 'draft' | 'published';
  commentOnly: boolean;
};

export type CompletenessResponse = {
  periodId: string;
  dueAt: string | null;
  overdue: boolean;
  rows: CompletenessRow[];
  byGrade: Record<number, CompletenessRow[]>;
};

/**
 * The one date `MyEntryTab` needs to render its deadline banner — not the office's full period
 * list (`listPeriods` is admin-only, on purpose). Null when there is no active school year, or no
 * period of it is open; the tab renders its empty state rather than treating that as an error.
 */
export type OpenReportPeriod = {
  id: string;
  name: string;
  startDate: string;
  endDate: string;
  entryOpensAt: string | null;
  dueAt: string | null;
  status: ReportPeriodStatus;
};

export type PeriodOpeningResult = {
  lines: number;
  cards: number;
  source: 'clone' | 'timetable';
};

export type CreateReportPeriodData = {
  schoolYearId: string;
  sequence: number;
  name: string;
  startDate: string;
  endDate: string;
  issueDate?: string;
  entryOpensAt?: string;
  dueAt?: string;
};

export type UpdateReportPeriodData = {
  sequence?: number;
  name?: string;
  startDate?: string;
  endDate?: string;
  issueDate?: string;
  entryOpensAt?: string;
  dueAt?: string;
};

export type CreateReportCardLineData = {
  gradeLevel: number;
  /** Stable across periods — never editable once created. */
  key: string;
  label: string;
  groupLabel?: string;
  subjectId?: string;
  scaleId?: string;
  kind?: ReportCardLineKind;
  description?: string;
  commentEnabled?: boolean;
  sortOrder?: number;
};

/** `gradeLevel` and `key` are absent on purpose: history joins through them, so neither is editable. */
export type UpdateReportCardLineData = {
  label?: string;
  groupLabel?: string | null;
  subjectId?: string | null;
  scaleId?: string | null;
  kind?: ReportCardLineKind;
  description?: string | null;
  commentEnabled?: boolean;
  /** False deactivates the line; it is never deleted. */
  active?: boolean;
};

/** A genuine partial update: a field left out is left unchanged server-side, `null` clears it. */
export type SaveMarkData = {
  valueId?: string | null;
  comment?: string | null;
};

/** One report card in a teacher's own list of the students they teach, newest first. */
export type StudentCardSummary = {
  cardId: string;
  periodId: string;
  periodName: string;
  sequence: number;
  schoolYearId: string;
  schoolYearName: string;
  status: 'draft' | 'published';
  access: 'full' | 'specialists';
};

export type ReportCardValue = { label: string; title: string };

export type ReportCardDocumentLine = {
  key: string;
  label: string;
  teachers: string[];
  description: string | null;
  value: ReportCardValue | null;
  comment: string | null;
};

export type ReportCardDocumentScale = {
  id: string;
  name: string;
  values: { id: string; label: string; title: string; descriptor: string | null; gpa?: number | null }[];
};

export type EarlyYearsSections = {
  banner: { title: string | null; focus: string | null; purpose: string | null };
  summaries: { title: string; body: string }[];
  domains: {
    domain: string;
    indicators: {
      indicatorId: string | null;
      code: string | null;
      label: string;
      description: string | null;
      value: ReportCardValue | null;
    }[];
  }[];
  reflection: { strengths: string | null; growth: string | null; homeSupport: string | null };
  specialists: ReportCardDocumentLine[];
};

export type StandardSections = {
  groups: { groupLabel: string | null; lines: ReportCardDocumentLine[] }[];
  homeroomComment: string | null;
  commentOnly: boolean;
};

export type ReportCardAttendanceSummary = {
  from: string;
  to: string;
  present: number;
  late: number;
  absent: number;
  excused: number;
  /** Days inside a suspension — never truancy, shown only when the student has any. */
  suspended: number;
  schoolDays: number;
  /** School days that carry a record; the rest were never marked. */
  recorded: number;
  rate: number;
};

/** Mirrors the API's `ReportCardDocument` — the same data the PDF is printed from. */
export type ReportCardDocument = {
  cardId: string;
  kind: 'early-years' | 'standard';
  student: { id: string; fullName: string; nickname: string | null; gradeLevel: number; gradeLabel: string };
  className: string | null;
  homeroomTeacher: string | null;
  period: {
    id: string;
    name: string;
    sequence: number;
    schoolYearId: string;
    schoolYearName: string;
    startDate: string;
    endDate: string;
  };
  issueDate: string | null;
  teachers: { subject: string; teachers: string[] }[];
  scales: ReportCardDocumentScale[];
  earlyYears: EarlyYearsSections | null;
  standard: StandardSections | null;
  attendance: ReportCardAttendanceSummary | null;
  hasMap: boolean;
};

export type CardView = { access: 'full' | 'specialists'; document: ReportCardDocument };

export const reportCardsApi = {
  listPeriods: (schoolYearId: string) =>
    fetchApi<ReportPeriod[]>(`${REPORT_PERIODS_PATH}?schoolYearId=${encodeURIComponent(schoolYearId)}`),
  createPeriod: (dto: CreateReportPeriodData) =>
    fetchApi<ReportPeriod>(REPORT_PERIODS_PATH, { method: 'POST', body: JSON.stringify(dto) }),
  updatePeriod: (id: string, dto: UpdateReportPeriodData) =>
    fetchApi<ReportPeriod>(`${REPORT_PERIODS_PATH}/${id}`, { method: 'PATCH', body: JSON.stringify(dto) }),
  openPeriod: (id: string) => fetchApi<PeriodOpeningResult>(`${REPORT_PERIODS_PATH}/${id}/open`, { method: 'POST' }),
  closePeriod: (id: string) => fetchApi<ReportPeriod>(`${REPORT_PERIODS_PATH}/${id}/close`, { method: 'POST' }),

  listLines: (periodId: string, gradeLevel: number) =>
    fetchApi<ReportCardLine[]>(`${REPORT_PERIODS_PATH}/${periodId}/lines?gradeLevel=${gradeLevel}`),
  createLine: (periodId: string, dto: CreateReportCardLineData) =>
    fetchApi<ReportCardLine>(`${REPORT_PERIODS_PATH}/${periodId}/lines`, { method: 'POST', body: JSON.stringify(dto) }),
  /**
   * `periodId` is not in the brief's produced signature, but the route is nested under it
   * (`PATCH /report-periods/:periodId/lines/:lineId`) — the API never scopes the write by it,
   * only routes on it, so it must still be supplied to reach the endpoint at all.
   */
  updateLine: (periodId: string, lineId: string, dto: UpdateReportCardLineData) =>
    fetchApi<ReportCardLine>(`${REPORT_PERIODS_PATH}/${periodId}/lines/${lineId}`, {
      method: 'PATCH',
      body: JSON.stringify(dto),
    }),
  setLineTeachers: (periodId: string, lineId: string, teacherIds: string[]) =>
    fetchApi<ReportCardLine>(`${REPORT_PERIODS_PATH}/${periodId}/lines/${lineId}/teachers`, {
      method: 'PUT',
      body: JSON.stringify({ teacherIds }),
    }),
  reorderLines: (periodId: string, gradeLevel: number, lineIds: string[]) =>
    fetchApi<void>(`${REPORT_PERIODS_PATH}/${periodId}/lines/reorder`, {
      method: 'POST',
      body: JSON.stringify({ gradeLevel, lineIds }),
    }),

  myGrids: (periodId: string, scope: EntryScope) =>
    fetchApi<EntryGridResponse[]>(`${REPORT_CARDS_PATH}/entry?periodId=${encodeURIComponent(periodId)}&scope=${scope}`),
  myEntrySummary: (periodId: string) =>
    fetchApi<EntrySummary>(`${REPORT_CARDS_PATH}/entry/summary?periodId=${encodeURIComponent(periodId)}`),
  myOpenPeriod: () => fetchApi<OpenReportPeriod | null>(`${REPORT_CARDS_PATH}/entry/period`),
  /** Omitting a field leaves it unchanged server-side; sending `null` clears it — never fill one
   *  in on the caller's behalf, or a grade-only save would blank out an existing comment. */
  saveMark: (cardId: string, lineId: string, dto: SaveMarkData) =>
    fetchApi<void>(`${REPORT_CARDS_PATH}/${cardId}/lines/${lineId}/mark`, {
      method: 'PUT',
      body: JSON.stringify(dto),
    }),

  completeness: (periodId: string) => fetchApi<CompletenessResponse>(`${REPORT_PERIODS_PATH}/${periodId}/completeness`),

  publishPeriod: (periodId: string, force: boolean) =>
    fetchApi<ReportCardJob>(`${REPORT_PERIODS_PATH}/${periodId}/publish`, {
      method: 'POST',
      body: JSON.stringify({ force }),
    }),
  publishProgress: (periodId: string) =>
    fetchApi<ReportCardJob | null>(`${REPORT_PERIODS_PATH}/${periodId}/publish/progress`),
  unpublishPeriod: (periodId: string) =>
    fetchApi<void>(`${REPORT_PERIODS_PATH}/${periodId}/unpublish`, { method: 'POST' }),
  /** Who has opened the period's published cards on the parent and student portals. */
  periodViews: (periodId: string) => fetchApi<PeriodViews>(`${REPORT_PERIODS_PATH}/${periodId}/views`),
  notifyPreview: (periodId: string) => fetchApi<NotifyPreview>(`${REPORT_PERIODS_PATH}/${periodId}/notify/preview`),
  notifyPeriod: (periodId: string) =>
    fetchApi<ReportCardJob>(`${REPORT_PERIODS_PATH}/${periodId}/notify`, { method: 'POST' }),
  notifyProgress: (periodId: string) =>
    fetchApi<ReportCardJob | null>(`${REPORT_PERIODS_PATH}/${periodId}/notify/progress`),
  remindPreview: (periodId: string) => fetchApi<NotifyPreview>(`${REPORT_PERIODS_PATH}/${periodId}/remind/preview`),
  remindPeriod: (periodId: string) =>
    fetchApi<ReportCardJob>(`${REPORT_PERIODS_PATH}/${periodId}/remind`, { method: 'POST' }),
  remindProgress: (periodId: string) =>
    fetchApi<ReportCardJob | null>(`${REPORT_PERIODS_PATH}/${periodId}/remind/progress`),
  listPeriodCards: (periodId: string, gradeLevel: number) =>
    fetchApi<PeriodCard[]>(`${REPORT_PERIODS_PATH}/${periodId}/cards?gradeLevel=${gradeLevel}`),
  setCommentOnly: (periodId: string, cardId: string, commentOnly: boolean) =>
    fetchApi<void>(`${REPORT_PERIODS_PATH}/${periodId}/cards/${cardId}/comment-only`, {
      method: 'PATCH',
      body: JSON.stringify({ commentOnly }),
    }),
  /** A published period's stored PDFs, a folder per grade; `gradeLevel` left out means every grade. */
  downloadPeriodZip: (periodId: string, gradeLevel?: number) =>
    fetchApiBlob(`${REPORT_PERIODS_PATH}/${periodId}/download.zip`, {
      method: 'POST',
      body: JSON.stringify({ gradeLevel }),
      timeoutMs: 300_000,
    }),
  /** The same cards joined into one PDF, to print in one go. */
  downloadPeriodPdf: (periodId: string, gradeLevel?: number) =>
    fetchApiBlob(`${REPORT_PERIODS_PATH}/${periodId}/download.pdf`, {
      method: 'POST',
      body: JSON.stringify({ gradeLevel }),
      timeoutMs: 300_000,
    }),
  /** The card's PDF as it would be published now, rendered on demand and never stored. */
  previewCardPdf: (cardId: string) => fetchApiBlob(`${REPORT_CARDS_PATH}/${cardId}/preview.pdf`),
  listScales: () => fetchApi<GradingScale[]>(GRADING_SCALES_PATH),

  /** The report cards of the students the caller teaches — `[]` when they teach none. */
  studentCards: (studentId: string) =>
    fetchApi<StudentCardSummary[]>(`${REPORT_CARDS_PATH}/students/${studentId}/cards`),
  cardView: (cardId: string) => fetchApi<CardView>(`${REPORT_CARDS_PATH}/cards/${cardId}/view`),
};
